<div class="order-management">
  <!-- Header Section -->
  <div class="page-header">
    <div class="header-content">
      <h1 class="page-title">
        <i class="fas fa-shopping-cart"></i>
        Order Management
      </h1>
      <div class="header-actions">
        <button class="btn btn-outline-primary" (click)="exportOrders()" [disabled]="exporting">
          <i class="fas fa-download"></i>
          {{ exporting ? 'Exporting...' : 'Export' }}
        </button>
        @if (error) {
          <button class="btn btn-primary" (click)="retry()">
          <i class="fas fa-redo"></i>
          Retry
        </button>
        }
      </div>
    </div>
  </div>

  <!-- Search and Filters -->
  @if (!isEditMode) {
    <div class="filters-section">
    <form [formGroup]="searchForm" class="filters-form">
      <div class="filters-grid">
        <div class="filter-row">
          <div class="filter-group">
            <label for="searchTerm" class="form-label">Search</label>
            <input
              type="text"
              id="searchTerm"
              formControlName="searchTerm"
              class="form-control"
              placeholder="Search by Order ID, Customer Name, Mobile, Address, Products, Status..."
            >
        </div>
        
          <div class="filter-group">
            <label for="status" class="form-label">Status</label>
            <select id="status" formControlName="status" class="form-select">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        
        <div class="filter-row">
          <div class="filter-group">
            <label for="paymentStatus" class="form-label">Payment</label>
            <select id="paymentStatus" formControlName="paymentStatus" class="form-select">
              <option value="">All Payments</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
        </div>
        
          <div class="filter-group">
            <label for="dateRange" class="form-label">Date Range</label>
            <select id="dateRange" formControlName="dateRange" class="form-select">
              <option value="">All Time</option>
              <option value="1">Last 24 Hours</option>
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 3 Months</option>
            </select>
          </div>
        </div>
        
        <div class="filter-actions">
          <div class="filter-info">
            <span class="filter-count">
              @if (hasActiveFilters()) {
                Showing {{ getFilteredOrdersCount() }} of {{ getTotalOrdersCount() }} orders
                <span class="active-filters">({{ getActiveFiltersCount() }} active filters)</span>
              } @else {
                Showing {{ getFilteredOrdersCount() }} of {{ getTotalOrdersCount() }} orders
                <span class="default-filter">(Default: Pending Orders)</span>
              }
            </span>
          </div>
          <div class="filter-buttons">
            <button type="button" class="clear-all-btn" (click)="clearAllFilters()" title="Clear All Filters">
              <i class="fas fa-times"></i>
              Clear All
            </button>
            <button type="button" class="clear-filters-btn" (click)="resetFiltersToDefault()" title="Reset to Default (Pending Orders)">
              <i class="fas fa-undo"></i>
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
  }

  <!-- Orders Table -->
  @if (!isEditMode) {
    <div class="orders-table-section">
    <div class="table-container">
      <!-- Loading State -->
      @if (loading) {
        <div class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading orders...</p>
      </div>
      }

      <!-- Error State -->
      @if (error && !loading) {
        <div class="error-state">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3>Failed to load orders</h3>
        <p>There was an error loading the orders. Please try again.</p>
        <button class="btn btn-primary" (click)="retry()">
          <i class="fas fa-redo"></i>
          Retry
        </button>
      </div>
      }

      <!-- Orders Table -->
      @if (!loading && !error) {
        <div class="table-responsive">
        <table class="table table-hover">
          <thead class="table-header">
            <tr>
                <th (click)="onSort('id')" class="sortable">
                  Order ID
                  <i [class]="getSortIcon('id')"></i>
                </th>
                <th (click)="onSort('userName')" class="sortable">
                  Customer Name
                  <i [class]="getSortIcon('userName')"></i>
                </th>
                <th (click)="onSort('products')" class="sortable">
                  Products
                  <i [class]="getSortIcon('products')"></i>
                </th>
                <th (click)="onSort('totalAmount')" class="sortable">
                  Total
                  <i [class]="getSortIcon('totalAmount')"></i>
                </th>
                <th (click)="onSort('status')" class="sortable">
                  Status
                  <i [class]="getSortIcon('status')"></i>
                </th>
                <th (click)="onSort('paymentStatus')" class="sortable">
                  Payment
                  <i [class]="getSortIcon('paymentStatus')"></i>
                </th>
                <th (click)="onSort('orderDate')" class="sortable">
                  Order Date
                  <i [class]="getSortIcon('orderDate')"></i>
                </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
              @for (order of filteredOrders | slice: (page - 1) * pageSize : page * pageSize; track order.id) {
                <tr class="order-row">
              <td>
                <div class="order-id">
                  <span class="id-text">{{ order.id }}</span>
                </div>
              </td>
              <td>
                <div class="customer-info">
                  <div class="customer-name">{{ order.userName }}</div>
                  <div class="customer-mobile">
                    <small class="text-muted">{{ order.userMobile }}</small>
                  </div>
                </div>
              </td>
              <td>
                <div class="products-info">
                  <div class="product-names">{{ getProductNames(order) }}</div>
                      <div class="order-type">
                        @if (order.isComboOrder) {
                          <span class="combo-order-badge">Combo Order</span>
                        } @else {
                          <span class="single-order-badge">Single Order</span>
                        }
                      </div>
                  <div class="dose-types">
                        @for (dose of order.doseTypes; track dose) {
                          <span class="text-muted">{{ dose }}</span>
                        }
                      </div>
                </div>
              </td>
              <td>
                <div class="amount-info">
                  <div class="total-amount">₹{{ getOrderTotal(order) }}</div>
                </div>
              </td>
              <td>
                <span class="status-badge" [ngClass]="getStatusClass(order.status)">
                  {{ getStatusText(order.status) }}
                </span>
              </td>
              <td>
                <span class="payment-badge" [ngClass]="getPaymentStatusClass(order.paymentStatus)">
                  {{ getPaymentStatusText(order.paymentStatus) }}
                </span>
              </td>
              <td>
                <div class="date-info">
                  <div class="order-date">{{ formatDate(order.orderDate) }}</div>
                      @if (order.shippingDate) {
                        <div class="shipping-date">
                    <small class="text-muted">Shipped: {{ formatDate(order.shippingDate) }}</small>
                  </div>
                      }
                </div>
              </td>
              <td>
                <div class="action-buttons">
                  <button 
                    class="btn btn-sm btn-outline-primary" 
                    (click)="viewOrderDetails(order.id)"
                    title="View Details">
                    <i class="fas fa-eye"></i>
                        View
                  </button>
                  <button 
                    class="btn btn-sm btn-outline-secondary" 
                    (click)="editOrder(order)"
                    title="Edit Order">
                    <i class="fas fa-edit"></i>
                        Edit
                  </button>
                </div>
              </td>
            </tr>
              }
          </tbody>
        </table>

        <!-- Empty State -->
          @if (filteredOrders.length === 0) {
            <div class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-shopping-cart"></i>
          </div>
          <h3>No orders found</h3>
          <p>No orders match your current filters. Try adjusting your search criteria.</p>
        </div>
          }
      </div>
      }
    </div>

    <!-- Built-in Pagination -->
    @if (!loading && !error && filteredOrders.length > pageSize) {
      <div class="pagination-section">
      <ngb-pagination
          [collectionSize]="collectionSize"
          [(page)]="page"
        [pageSize]="pageSize"
        [boundaryLinks]="true"
        [maxSize]="5"
        class="d-flex justify-content-center">
      </ngb-pagination>
      
      <div class="pagination-info">
          Showing {{ (page - 1) * pageSize + 1 }} to 
          {{ (page * pageSize > collectionSize ? collectionSize : page * pageSize) }} of {{ collectionSize }} orders
        </div>
      </div>
    }
  </div>
  }

  <!-- Edit Order Section -->
  @if (isEditMode && editingOrder) {
    <div class="edit-order-section">
      <div class="edit-header">
        <h2><i class="fas fa-edit"></i> Edit Order</h2>
        <div class="edit-actions">
          <button class="btn btn-outline-secondary" (click)="cancelEdit()">
            <i class="fas fa-times"></i>
            Cancel
          </button>
          <button class="btn btn-primary" (click)="saveEdit()" [disabled]="loading">
            <i class="fas fa-save"></i>
            {{ loading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>

      <div class="edit-form-grid">
        <!-- Order Information -->
        <div class="edit-card">
          <div class="card-header">
            <h3><i class="fas fa-info-circle"></i> Order Information</h3>
          </div>
          <div class="card-content">
            <div class="form-row">
              <div class="form-group">
                <label>Order ID:</label>
                <span class="order-id disabled-field">{{ editingOrder.id }}</span>
              </div>
              <div class="form-group">
                <label>Order Date:</label>
                <span class="disabled-field">{{ formatDate(editingOrder.orderDate) }}</span>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Order Type:</label>
                <span class="order-type-badge disabled-field" [ngClass]="editingOrder.isComboOrder ? 'combo' : 'single'">
                  {{ editingOrder.isComboOrder ? 'Combo Order' : 'Single Order' }}
                </span>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Product Amount:</label>
                <span class="amount disabled-field">₹{{ getProductAmount(editingOrder) }}</span>
              </div>
              <div class="form-group">
                <label>Delivery Charges:</label>
                <span class="amount disabled-field">₹{{ getDeliveryCharges(editingOrder) }}</span>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Grand Total:</label>
                <span class="amount disabled-field grand-total">₹{{ getGrandTotal(editingOrder) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Customer Information -->
        <div class="edit-card">
          <div class="card-header">
            <h3><i class="fas fa-user"></i> Customer Information</h3>
          </div>
          <div class="card-content">
            <div class="form-row">
              <div class="form-group">
                <label>Name:</label>
                <span class="disabled-field">{{ editingOrder.userName }}</span>
              </div>
              <div class="form-group">
                <label>Mobile:</label>
                <span class="disabled-field">{{ editingOrder.userMobile }}</span>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Address:</label>
                <span class="address disabled-field">{{ editingOrder.deliveryAddress }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Status -->
        <div class="edit-card">
          <div class="card-header">
            <h3><i class="fas fa-tasks"></i> Order Status</h3>
          </div>
          <div class="card-content">
            <div class="form-row">
              <div class="form-group">
                <label>Current Status:</label>
                <select [(ngModel)]="editingOrder.status" class="form-control" (change)="onStatusChange()">
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div class="form-group">
                <label>Payment Status:</label>
                <span class="payment-badge disabled-field" [ngClass]="getPaymentStatusClass(editingOrder.paymentStatus)">
                  {{ getPaymentStatusText(editingOrder.paymentStatus) }}
                </span>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Payment Method:</label>
                <span class="disabled-field">{{ editingOrder.paymentMethod }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Shipping Information -->
        <div class="edit-card">
          <div class="card-header">
            <h3><i class="fas fa-shipping-fast"></i> Shipping Information</h3>
          </div>
          <div class="card-content">
            <div class="form-row">
              <div class="form-group">
                <label>Tracking Number: @if (editingOrder.status === 'shipped') {<span class="required">*</span>}</label>
                <input 
                  type="text" 
                  [(ngModel)]="editingOrder.trackingNumber" 
                  placeholder="Enter tracking number" 
                  class="form-control"
                  [class.is-invalid]="editingOrder.status === 'shipped' && !editingOrder.trackingNumber"
                  [class.is-valid]="editingOrder.status === 'shipped' && editingOrder.trackingNumber">
                @if (editingOrder.status === 'shipped' && !editingOrder.trackingNumber) {
                <div class="invalid-feedback">
                  Tracking number is required when status is shipped
                </div>
                }
              </div>
              <div class="form-group">
                <label>Shipping Provider:</label>
                <select [(ngModel)]="editingOrder.shippingProvider" class="form-control">
                  <option value="">Select provider</option>
                  <option value="shiprocket">Shiprocket</option>
                  <option value="delhivery">Delhivery</option>
                  <option value="bluedart">Blue Dart</option>
                  <option value="dtdc">DTDC</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Estimated Delivery:</label>
                <input 
                  type="date" 
                  [(ngModel)]="editingOrder.estimatedDelivery" 
                  class="form-control">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Products Section -->
      <div class="products-section">
        <div class="section-header">
          <h3><i class="fas fa-box"></i> Products</h3>
        </div>
        <div class="products-grid">
          @for (product of editingOrder.products; track product.id) {
            <div class="product-card">
              <div class="product-image">
                <img [src]="getDoseImage(product.doseType)" [alt]="getLocalizedName(product)">
              </div>
              <div class="product-details">
                <h4 class="product-name">{{ getLocalizedName(product) }}</h4>
                <p class="product-description">{{ getLocalizedDescription(product) }}</p>
                <div class="product-meta">
                  <span class="product-price">₹{{ product.price ?? 0 }}</span>
                </div>
                @if (product.doseType) {
                  <span class="dose-type-badge">{{ product.doseType }}</span>
                }
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Notes Section -->
      <div class="notes-section">
        <div class="section-header">
          <h3><i class="fas fa-sticky-note"></i> Notes</h3>
        </div>
        <div class="notes-content">
          <textarea 
            [(ngModel)]="editingOrder.notes" 
            placeholder="Add any additional notes about this order..."
            class="notes-textarea"
            rows="4">
          </textarea>
        </div>
      </div>
    </div>
  }

  <!-- Status Update Modal -->
  @if (selectedOrder) {
    <div class="modal fade" id="statusUpdateModal" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-edit"></i>
            Update Order Status
          </h5>
          <button type="button" class="btn-close" (click)="closeStatusModal()"></button>
        </div>
        
        <div class="modal-body">
          <div class="order-summary mb-4">
            <h6>Order Summary</h6>
            <div class="row">
              <div class="col-md-6">
                  <p><strong>Order ID:</strong> {{ selectedOrder.id }}</p>
                  <p><strong>Customer:</strong> {{ selectedOrder.userName }}</p>
                  <p><strong>Total Amount:</strong> ₹{{ getOrderTotal(selectedOrder) }}</p>
              </div>
              <div class="col-md-6">
                <p><strong>Current Status:</strong> 
                    <span class="status-badge" [ngClass]="getStatusClass(selectedOrder.status)">
                      {{ getStatusText(selectedOrder.status) }}
                  </span>
                </p>
                <p><strong>Payment Status:</strong> 
                    <span class="payment-badge" [ngClass]="getPaymentStatusClass(selectedOrder.paymentStatus)">
                      {{ getPaymentStatusText(selectedOrder.paymentStatus) }}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <form [formGroup]="statusUpdateForm" class="status-form">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="form-group">
                  <label for="modalStatus" class="form-label">Order Status *</label>
                  <select id="modalStatus" formControlName="status" class="form-select">
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group">
                  <label for="modalTracking" class="form-label">Tracking Number</label>
                  <input
                    type="text"
                    id="modalTracking"
                    formControlName="trackingNumber"
                    class="form-control"
                    placeholder="Enter tracking number"
                  >
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group">
                  <label for="modalProvider" class="form-label">Shipping Provider</label>
                  <select id="modalProvider" formControlName="shippingProvider" class="form-select">
                    <option value="">Select provider</option>
                    <option value="shiprocket">Shiprocket</option>
                    <option value="delhivery">Delhivery</option>
                    <option value="bluedart">Blue Dart</option>
                    <option value="dtdc">DTDC</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group">
                  <label for="modalDelivery" class="form-label">Estimated Delivery</label>
                  <input
                    type="date"
                    id="modalDelivery"
                    formControlName="estimatedDelivery"
                    class="form-control"
                  >
                </div>
              </div>
              
              <div class="col-12">
                <div class="form-group">
                  <label for="modalNotes" class="form-label">Notes</label>
                  <textarea
                    id="modalNotes"
                    formControlName="notes"
                    class="form-control"
                    rows="3"
                    placeholder="Add any additional notes..."
                  ></textarea>
                </div>
              </div>
            </div>
          </form>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeStatusModal()">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            (click)="updateOrderStatus()"
            [disabled]="!statusUpdateForm.valid">
            <i class="fas fa-save"></i>
            Update Status
          </button>
        </div>
      </div>
    </div>
  </div>
  }

  <!-- Courier Selection Modal -->
  @if (showCourierModal) {
    <div class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-truck"></i>
              Select Courier Service
            </h5>
            <button type="button" class="btn-close" (click)="closeCourierModal()"></button>
          </div>
          
          <div class="modal-body">
            @if (loadingCouriers) {
              <div class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading available couriers...</p>
              </div>
            } @else if (availableCouriers.length === 0) {
              <div class="alert alert-warning">
                <i class="fas fa-exclamation-triangle"></i>
                No courier services available for this delivery address.
              </div>
            } @else {
              <div class="courier-selection">
                <p class="text-muted mb-3">
                  <i class="fas fa-info-circle"></i>
                  Select a courier service for this order. Recommended courier is highlighted.
                </p>
                
                <div class="courier-list">
                  @for (courier of availableCouriers; track courier.id) {
                    <div 
                      class="courier-card" 
                      [class.selected]="selectedCourierId === courier.courier_company_id"
                      [class.recommended]="courier.courier_company_id === availableCouriers[0]?.courier_company_id"
                      (click)="selectedCourierId = courier.courier_company_id">
                      <div class="courier-header">
                        <div class="courier-name-section">
                          <input 
                            type="radio" 
                            [id]="'courier-' + courier.courier_company_id"
                            [value]="courier.courier_company_id"
                            [(ngModel)]="selectedCourierId"
                            class="form-check-input">
                          <label [for]="'courier-' + courier.courier_company_id" class="courier-name">
                            {{ courier.courier_name }}
                            @if ($index === 0) {
                              <span class="badge bg-success ms-2">Recommended</span>
                            }
                          </label>
                        </div>
                        <div class="courier-rate">
                          <strong>₹{{ courier.rate }}</strong>
                        </div>
                      </div>
                      
                      <div class="courier-details">
                        <div class="row g-2">
                          <div class="col-md-6">
                            <small class="text-muted">
                              <i class="fas fa-calendar"></i>
                              Estimated Delivery: {{ courier.etd || courier.estimated_delivery_days + ' days' }}
                            </small>
                          </div>
                          <div class="col-md-6">
                            <small class="text-muted">
                              <i class="fas fa-weight"></i>
                              Max Weight: {{ courier.surface_max_weight || courier.air_max_weight || 'N/A' }} kg
                            </small>
                          </div>
                          <div class="col-md-6">
                            <small class="text-muted">
                              <i class="fas fa-money-bill-wave"></i>
                              COD: {{ courier.cod ? 'Available' : 'Not Available' }}
                            </small>
                          </div>
                          <div class="col-md-6">
                            <small class="text-muted">
                              <i class="fas fa-star"></i>
                              Rating: {{ courier.rating || 'N/A' }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="closeCourierModal()" [disabled]="loading">
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              (click)="confirmCourierSelection()"
              [disabled]="!selectedCourierId || loading || availableCouriers.length === 0">
              <i class="fas fa-check"></i>
              {{ loading ? 'Creating...' : 'Create Shipment' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</div> 